/**
 * BottomSheet.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Bottom sheet genérico e reutilizável para mobile.
 *
 * @param {boolean}          props.isOpen      - Se o sheet está aberto
 * @param {Function}         props.onClose     - Callback ao fechar
 * @param {string}           [props.title]     - Título opcional no header
 * @param {React.ReactNode}  props.children    - Conteúdo interno
 * @param {'auto'|'half'|'full'} [props.height='auto'] - Altura do sheet
 * @param {boolean}          [props.showHandle=true]   - Exibir handle de arraste
 *
 * @module BottomSheet
 */

import React, { useEffect, useCallback, useRef, useState } from 'react';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import styles from './BottomSheet.module.css';

/**
 * Bottom sheet genérico com animação spring, overlay e suporte a safe-area.
 *
 * @param {Object} props
 * @returns {React.ReactElement|null}
 */
export default function BottomSheet({
  isOpen,
  onClose,
  title,
  children,
  height = 'auto',
  showHandle = true,
}) {
  // Controla a animação de saída antes de desmontar
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const sheetRef = useRef(null);

  // ── Montar / desmontar com animação ────────────────────────────────────────

  useEffect(() => {
    if (isOpen) {
      setIsClosing(false);
      setIsVisible(true);
    } else if (isVisible) {
      // Inicia animação de saída
      setIsClosing(true);
      const timer = setTimeout(() => {
        setIsVisible(false);
        setIsClosing(false);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fechar com Escape ───────────────────────────────────────────────────────

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape') onClose?.();
  }, [onClose]);

  useEffect(() => {
    if (isVisible) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [isVisible, handleKeyDown]);

  // ── Bloquear scroll do body quando aberto ──────────────────────────────────

  useEffect(() => {
    if (isVisible) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
  }, [isVisible]);

  if (!isVisible) return null;

  // ── Classes de altura ───────────────────────────────────────────────────────

  const heightClass = {
    auto: styles.heightAuto,
    half: styles.heightHalf,
    full: styles.heightFull,
  }[height] || styles.heightAuto;

  return (
    <>
      {/* ── Overlay ──────────────────────────────────────────────────────────── */}
      <div
        className={`${styles.overlay} ${isClosing ? styles.overlayClosing : ''}`}
        onClick={onClose}
        role="presentation"
        aria-hidden="true"
      />

      {/* ── Sheet ────────────────────────────────────────────────────────────── */}
      <div
        ref={sheetRef}
        className={`${styles.sheet} ${heightClass} ${isClosing ? styles.sheetClosing : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={title || 'Painel'}
      >
        {/* Handle de arraste */}
        {showHandle && (
          <div className={styles.handleWrapper} aria-hidden="true">
            <div className={styles.handle} />
          </div>
        )}

        {/* Header com título e botão fechar */}
        {title && (
          <div className={styles.header}>
            <h2 className={styles.title}>{title}</h2>
            <button
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Fechar"
              type="button"
            >
              <CloseOutlinedIcon fontSize="small" />
            </button>
          </div>
        )}

        {/* Conteúdo */}
        <div className={styles.content}>
          {children}
        </div>
      </div>
    </>
  );
}
